import axios from 'axios'
import { API_BASE_URL } from '@/shared/config/env'
import { Cookies } from '@/shared/helpers/Cookies'

export abstract class ApiHandler {
  private static interceptorsSetup = false

  /** Обычный запрос: 30 секунд с запасом на медленную мобильную сеть. */
  private static readonly REQUEST_TIMEOUT_MS = 30_000

  /** Загрузка файлов идёт дольше — отдельный, больший лимит. */
  private static readonly UPLOAD_TIMEOUT_MS = 180_000

  private static setupInterceptors() {
    if (this.interceptorsSetup) return

    axios.interceptors.request.use((config) => {
      const token = Cookies.get('authToken')
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
      return config
    })

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const cookieOptions = {
            domain: window.location.hostname,
            path: '/',
          }
          Cookies.remove('userId', cookieOptions)
          Cookies.remove('authToken', cookieOptions)
          window.location.href = '/'
          return Promise.reject(error)
        }
        return Promise.reject(error)
      },
    )

    this.interceptorsSetup = true
  }

  public static get(path: string, parameters: any = null): Promise<any> {
    return this.request('GET', path, parameters)
  }

  public static post(
    path: string,
    parameters: any,
    isFormData?: boolean,
    onUploadProgress?: (e: any) => void,
  ): Promise<any> {
    return this.request('POST', path, parameters, isFormData, onUploadProgress)
  }

  public static patch(path: string, parameters: any, isFormData?: boolean): Promise<any> {
    return this.request('PATCH', path, parameters, isFormData)
  }

  public static put(path: string, parameters: any, isFormData?: boolean): Promise<any> {
    return this.request('PUT', path, parameters, isFormData)
  }

  public static delete(path: string, parameters: any): Promise<any> {
    return this.request('DELETE', path, parameters)
  }

  public static request(
    method: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT',
    path: string,
    parameters: any,
    isFormData?: boolean,
    onUploadProgress?: (e: any) => void,
  ): Promise<any> {
    this.setupInterceptors()

    return new Promise<any>((resolve, reject) => {
      const baseURL = API_BASE_URL
      const config: any = {
        method,
        url: `${baseURL}${path}`,
        // Без таймаута «залипшее» мобильное соединение (сокет открыт, ответа
        // нет) держало запрос бесконечно: вечный спиннер, а отправка сообщений
        // блокировалась до перезагрузки страницы.
        // Загрузка файлов идёт дольше, поэтому ей отдельный, больший лимит.
        timeout: isFormData ? ApiHandler.UPLOAD_TIMEOUT_MS : ApiHandler.REQUEST_TIMEOUT_MS,
      }

      // Прогресс отдачи файла на сервер (для прогресс-бара загрузки).
      if (onUploadProgress) {
        config.onUploadProgress = onUploadProgress
      }

      if (method === 'GET') {
        if (parameters && Object.keys(parameters).length) {
          config.params = parameters
        }
      } else {
        let hasFile = false
        if (parameters) {
          for (const key of Object.keys(parameters)) {
            const value = parameters[key]
            if (typeof window !== 'undefined' && 'File' in window && value instanceof File) {
              hasFile = true
              break
            }
          }
        }

        let data: any = null

        if (hasFile) {
          const formData = new FormData()
          for (const key of Object.keys(parameters)) {
            const value = parameters[key]
            if (value !== null && value !== undefined) {
              formData.append(key, value)
            }
          }
          data = formData
        } else if (isFormData) {
          data = parameters
          config.headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
        } else if (parameters && Object.keys(parameters).length) {
          data = {}
          for (const key of Object.keys(parameters)) {
            const value = parameters[key]
            if (value !== null && value !== undefined) {
              data[key] = value
            }
          }
          config.headers = { 'Content-Type': 'application/json' }
        }

        config.data = data
      }

      axios(config)
        // Успех определяется кодом ответа, а не содержимым тела. Раньше любой
        // ответ с полем message считался ошибкой — и удачная отписка от канала
        // («Вы отписались от канала», HTTP 200) показывалась как сбой.
        // Неуспешные коды axios отклоняет сам.
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }
}